import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cloud, CheckCircle2, XCircle, Loader2, RefreshCw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const VERIFY_LABELS = {
  data_streams: "Data Streams", identity_resolution: "Identity Resolution Rulesets", segments: "Segments",
};

export default function RealOrgVerificationTab({
  apiPrefix = "/salesforce", connectPath = "/salesforce/connect", verifyLabels = VERIFY_LABELS,
  verifyPath, heading = "Go Beyond the Simulator",
  description = "Connect your own real Salesforce Data Cloud org and we'll verify — via live, read-only API calls — that you've actually built this setup for real.",
  connectLabel = "Connect Your Org",
}) {
  const [status, setStatus] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`${apiPrefix}/status`).then((r) => setStatus(r.data));
  }, [apiPrefix]);

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post(verifyPath || `${apiPrefix}/verify`);
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!status) return <div className="flex justify-center py-16" data-testid="real-org-tab-loading"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>;

  if (!status.connected) {
    return (
      <div className="glass-panel rounded-2xl p-10 text-center" data-testid="real-org-tab-not-connected">
        <Cloud className="w-8 h-8 text-sky-400 mx-auto mb-4" />
        <h3 className="font-heading text-lg font-semibold text-white mb-2">{heading}</h3>
        <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">{description}</p>
        <Link to={connectPath} data-testid="real-org-connect-link" className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
          {connectLabel} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-2xl p-6" data-testid="real-org-tab-connected">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-400">Connected to your org: <span className="text-white">{status.instance_url || status.rest_instance_url}</span></p>
        <button onClick={verify} disabled={verifying} data-testid="real-org-verify-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
          {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Verify My Setup
        </button>
      </div>
      {result && (
        <div className="space-y-2.5 mt-4" data-testid="real-org-verify-results">
          {Object.entries(result).map(([key, r]) => (
            <div key={key} className="flex items-start gap-2.5" data-testid={`real-org-verify-${key}`}>
              {r.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />}
              <div>
                <p className="text-sm text-white">{verifyLabels[key] || key} {r.verified && `(${r.count} found)`}</p>
                {r.error && <p className="text-xs text-slate-500 mt-0.5">{r.error}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
